import { DateTime } from 'luxon'

import ChannelQ from '@/queries/ChannelQ'
import CourseQ from '@/queries/CourseQ'
import KeyClubClubQ from '@/queries/KeyClubClubQ'
import KeyClubDistrictQ from '@/queries/KeyClubDistrictQ'
import UserQ from '@/queries/UserQ'
import WorldQ from '@/queries/WorldQ'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { getActiveAnniversary } from '@/client/services/user'

import { channelBaseFromQuery, worldBaseFromQuery } from './helpers'
import type { ChannelGetter, UserGetter, WorldGetter } from './types'

export const USER_QUERY_TO_FIELDS: UserGetter = {
  id: ({ query }) => query(UserQ.id) ?? 0,
  avatarUrl: ({ query }) => query(UserQ.avatar_url) ?? '',
  displayName: ({ query }) => query(UserQ.display_name) ?? '',
  bio: ({ query }) => query(UserQ.bio) ?? '',
  username: ({ query }) => query(UserQ.username),
  pronouns: ({ query }) => {
    const pronouns = query(UserQ.pronouns) ?? ''
    return pronouns.length === 0 ? null : pronouns
  },
  roles: ({ query }) => query(UserQ.roles) ?? [],
  points: ({ query }) => query(UserQ.points) ?? 0,
  timezone: ({ query }) => query(UserQ.timezone) ?? '',
  activeAnniversary: ({ query }) => {
    const memberSince = query(UserQ.member_since)
    return getActiveAnniversary(DateTime.fromISO(`${memberSince}`))
  },
  keyClubDistrictName: ({ query }) => {
    const keyClubClub = query.one(
      KeyClubClubQ.where({
        id: UserQ.key_club_club_id,
        include: [
          {
            association: 'KeyClubDistrict',
            required: false,
          },
        ],
      })
    )
    return keyClubClub(KeyClubDistrictQ.name)
  },
  collegeInfo: ({ query }) => query(UserQ.getFilteredCollegeInfo()),
}

export const WORLD_QUERY_TO_FIELDS: WorldGetter = {
  id: ({ query }) => query(WorldQ.id) ?? 0,
  title: ({ query }) => query(WorldQ.title) ?? '',
  slug: ({ query }) => query(WorldQ.slug) ?? '',
  iconUrl: ({ query }) => query(WorldQ.icon_url) ?? '',
  primaryColor: ({ query }) => query(WorldQ.primary_color) ?? '',
  description: ({ query }) => query(WorldQ.description) ?? EMPTY_RICH_TEXT_VALUE,
  guidelines: ({ query }) => query(WorldQ.guidelines) ?? EMPTY_RICH_TEXT_VALUE,
  memberCount: ({ query }) => query(WorldQ.countMembers()) ?? 0,
  postCount: ({ query }) => query(WorldQ.countPosts()) ?? 0,
  visibility: ({ query }) => query(WorldQ.visibility) ?? 'hidden',
  domain: ({ query }) => query(WorldQ.domain) ?? 'Other Subjects',
  courseName: ({ query }) => {
    const course = query.one(CourseQ.where({ id: WorldQ.course_id }))
    return course(CourseQ.name)
  },
  position: ({ query }) => query(WorldQ.position) ?? 0,
  channels: ({ query, userId }) => {
    const channels = query.many(
      ChannelQ.where(
        {
          worldId: WorldQ.id,
          order: [[ChannelQ.position, 'asc']],
        },
        userId == null ? undefined : ChannelQ.isVisibleToUser({ userId })
      )
    )

    return channels.map(channel => channelBaseFromQuery({ channel }))
  },
  sessionTemplatesCount: ({ query, userId }) =>
    query(WorldQ.countSessionTemplates({ userId })) ?? 0,
  seriesTemplatesCount: ({ query, userId }) => query(WorldQ.countSeriesTemplates({ userId })) ?? 0,
}

export const CHANNEL_QUERY_TO_FIELDS: ChannelGetter = {
  id: ({ query }) => query(ChannelQ.id) ?? 0,
  title: ({ query }) => query(ChannelQ.title) ?? '',
  type: ({ query }) => query(ChannelQ.type) ?? 'discussion',
  worldLevel: ({ query }) => query(ChannelQ.world_level),
  world: ({ query }) => worldBaseFromQuery({ world: query }),
  isMember: ({ query, userId }) => query(ChannelQ.isUserMember(userId)) ?? false,
  postCount: ({ query }) => query(ChannelQ.countPosts()) ?? 0,
  memberCount: ({ query }) => query(ChannelQ.countMembers()) ?? 0,
  description: ({ query }) => query(ChannelQ.description) ?? EMPTY_RICH_TEXT_VALUE,
  isArchived: ({ query }) => query(ChannelQ.archived_at) != null,
}
